import Interceptor from "./Interceptor";

export const getSiteBackups = async (siteId, params = {}) => {
  try {
    const response = await Interceptor.get(`/backups/sites/${siteId}`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllSitesBackups = async (params = {}) => {
  try {
    const response = await Interceptor.get(`/backups`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createBackup = async (siteId, payload) => {
  try {
    const response = await Interceptor.post(
      `/backups/sites/${siteId}/create`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const restoreBackup = async (backupId, options = {}) => {
  try {
    const response = await Interceptor.post(
      `/backups/${backupId}/restore`,
      options,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteBackup = async (backupId) => {
  try {
    const response = await Interceptor.delete(`/backups/${backupId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadBackup = async (backupId, fileType = "files") => {
  try {
    const response = await Interceptor.get(
      `/backups/${backupId}/download/${fileType}`,
      {
        responseType: "blob",
        timeout: 300000, // 5 minutes timeout for large files
      },
    );

    // Check if the response is actually a JSON error (blob with application/json type)
    if (
      response.data instanceof Blob &&
      response.data.type === "application/json"
    ) {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        if (json.error) {
          throw new Error(json.error);
        }
      } catch (parseError) {
        // If parseError has a message from our json.error throw, rethrow it
        if (parseError instanceof Error && parseError.message) {
          throw parseError;
        }
        // Otherwise JSON parsing failed — response is not valid JSON, continue normally
      }
    }

    // Create blob URL for download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Get filename from response headers or generate one
    const contentDisposition = response.headers["content-disposition"];
    let filename = `backup_${backupId}_${fileType}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      } else {
        const filenameMatch2 = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch2) {
          filename = filenameMatch2[1];
        }
      }
    }

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (error) {
    // Handle blob error responses from axios
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        throw new Error(json.error || "Failed to download backup");
      } catch (parseError) {
        if (parseError.message && parseError.message !== "Unexpected token") {
          throw parseError;
        }
      }
    }

    // Handle specific error cases
    if (error.response?.status === 404) {
      throw new Error("Backup file not found on server");
    } else if (error.response?.status === 403) {
      throw new Error("You don't have permission to download this backup");
    } else if (error.code === "ECONNABORTED") {
      throw new Error("Download timeout - file might be too large");
    } else {
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          "Failed to download backup",
      );
    }
  }
};

export const getDownloadUrl = (backupId, fileType = "files") => {
  // This creates a direct download URL that can be used with window.open
  const baseUrl = process.env.REACT_APP_API_URL || "";
  return `${baseUrl}/api/backups/${backupId}/download/${fileType}`;
};

export const getBackupSchedule = async (siteId) => {
  try {
    const response = await Interceptor.get(`/backups/sites/${siteId}/schedule`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateBackupSchedule = async (siteId, payload) => {
  try {
    const response = await Interceptor.put(
      `/backups/sites/${siteId}/schedule`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
